// Stałe, walidacja i pomocnicze funkcje dla zadań podcast-core.
// Źródło prawdy: servers/podcast-core/API_CONTRACT.md („Endpointy panelu”, „Kreator (v2)”)
// oraz src/validate.js, src/source.js, src/hosts.js, src/tones.js, src/languages.js.

export const POLL_INTERVAL_MS = 2000;

export const PODCASTS_PATH = '/writer/podcasts';

export const SOURCE_TYPES = ['url', 'text', 'file'];

export const URL_MAX_CHARS = 2048;
export const TEXT_MIN_CHARS = 300;
export const TEXT_MAX_CHARS = 100000;
export const TITLE_MAX_CHARS = 200;
export const FILE_MAX_BYTES = 5 * 1024 * 1024;
export const FILE_EXTENSIONS = ['txt', 'md', 'pdf', 'docx'];
export const FILE_ACCEPT = '.txt,.md,.markdown,.pdf,.docx';

export const MIN_SECONDS = 30;
export const MAX_SECONDS = 600;

// Fallback, gdy GET /api/public/config się nie powiedzie (wartości zgodne z kontraktem).
export const FALLBACK_CONFIG = {
  languages: [
    { code: 'pl', label: 'Polski' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'es', label: 'Español' },
    { code: 'cs', label: 'Čeština' }
  ],
  tones: [
    { code: 'professional', label: 'Profesjonalny' },
    { code: 'neutral', label: 'Neutralny' },
    { code: 'casual', label: 'Swobodny' },
    { code: 'expressive', label: 'Ekspresyjny' }
  ],
  durations: [
    { seconds: 60, label: '1 min', free: true },
    { seconds: 180, label: '3 min', free: false },
    { seconds: 300, label: '5 min', free: false },
    { seconds: 600, label: '10 min', free: false }
  ],
  hosts: [
    { id: 'ola', name: 'Ola', gender: 'female', tagline: 'Prowadząca, która zadaje właściwe pytania', sample: 'hosts/ola.wav' },
    { id: 'marta', name: 'Marta', gender: 'female', tagline: 'Ciepło i spokojnie, jak rozmowa przy kawie', sample: 'hosts/marta.wav' },
    { id: 'zuza', name: 'Zuza', gender: 'female', tagline: 'Szybkie tempo i konkrety, idealna do tech', sample: 'hosts/zuza.wav' },
    { id: 'tomek', name: 'Tomek', gender: 'male', tagline: 'Ekspert, który tłumaczy zawiłe tematy po ludzku', sample: 'hosts/tomek.wav' },
    { id: 'kuba', name: 'Kuba', gender: 'male', tagline: 'Energia, empatia i trochę humoru', sample: 'hosts/kuba.wav' },
    { id: 'piotr', name: 'Piotr', gender: 'male', tagline: 'Spokojny i rzeczowy, gdy liczy się zaufanie', sample: 'hosts/piotr.wav' }
  ],
  defaultHosts: { A: 'ola', B: 'tomek' },
  defaults: { language: 'pl', tone: 'neutral', seconds: 60 }
};

const nonEmptyArray = (value) => (Array.isArray(value) && value.length > 0 ? value : null);

// Łączy odpowiedź /api/public/config z fallbackiem (brakujące / puste pola -> fallback).
export const normalizeConfig = (raw) => {
  const data = raw && typeof raw === 'object' ? raw : {};
  const hosts = nonEmptyArray(data.hosts) || FALLBACK_CONFIG.hosts;
  const hostIds = new Set(hosts.map((host) => host.id));
  const defaultHosts = data.defaultHosts && hostIds.has(data.defaultHosts.A) && hostIds.has(data.defaultHosts.B)
    && data.defaultHosts.A !== data.defaultHosts.B
    ? data.defaultHosts
    : { A: hosts[0].id, B: (hosts.find((host) => host.id !== hosts[0].id) || hosts[0]).id };
  const defaults = { ...FALLBACK_CONFIG.defaults, ...(data.defaults || {}) };
  const seconds = Number(defaults.seconds);
  return {
    languages: nonEmptyArray(data.languages) || FALLBACK_CONFIG.languages,
    tones: nonEmptyArray(data.tones) || FALLBACK_CONFIG.tones,
    durations: nonEmptyArray(data.durations) || FALLBACK_CONFIG.durations,
    hosts,
    defaultHosts,
    defaults: {
      ...defaults,
      seconds: Number.isInteger(seconds) && seconds >= MIN_SECONDS && seconds <= MAX_SECONDS ? seconds : 60
    }
  };
};

// Kroki pokazywane użytkownikowi + próg `progress`, przy którym krok się zaczyna.
export const PIPELINE_STEPS = [
  { key: 'fetching', progress: 10 },
  { key: 'scripting', progress: 30 },
  { key: 'directing', progress: 55 },
  { key: 'voicing', progress: 75 },
  { key: 'done', progress: 100 }
];

export const isJobActive = (job) => !!job && (job.status === 'queued' || job.status === 'running');

export const isValidHttpUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed || trimmed.length > URL_MAX_CHARS) return false;
  try {
    const parsed = new URL(trimmed);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && !!parsed.hostname;
  } catch (error) {
    return false;
  }
};


export const fileExtension = (name) => {
  const match = /\.([a-z0-9]+)$/i.exec(name || '');
  const ext = match ? match[1].toLowerCase() : '';
  return ext === 'markdown' ? 'md' : ext;
};

export const normalizeText = (value) => (value || '').replace(/\r\n?/g, '\n').trim();

/**
 * Walidacja formularza zgodna z serwerem. Zwraca obiekt { pole: kluczTłumaczenia } (pusty = OK).
 * values: { sourceType, url, text, title, file, language, tone, seconds, hostA, hostB }
 */
export const validatePodcastForm = (values, config) => {
  const errors = {};
  const cfg = config || FALLBACK_CONFIG;

  if (values.sourceType === 'url') {
    if (!isValidHttpUrl(values.url)) errors.url = 'podcasts.validation.url';
  } else if (values.sourceType === 'text') {
    const length = normalizeText(values.text).length;
    if (length < TEXT_MIN_CHARS) errors.text = 'podcasts.validation.textShort';
    else if (length > TEXT_MAX_CHARS) errors.text = 'podcasts.validation.textLong';
    if ((values.title || '').trim().length > TITLE_MAX_CHARS) errors.title = 'podcasts.validation.titleLong';
  } else if (values.sourceType === 'file') {
    const file = values.file;
    if (!file) errors.file = 'podcasts.validation.fileMissing';
    else if (!FILE_EXTENSIONS.includes(fileExtension(file.name))) errors.file = 'podcasts.validation.fileType';
    else if (!file.size) errors.file = 'podcasts.validation.fileEmpty';
    else if (file.size > FILE_MAX_BYTES) errors.file = 'podcasts.validation.fileSize';
  } else {
    errors.source = 'podcasts.validation.source';
  }

  if (!cfg.languages.some((language) => language.code === values.language)) {
    errors.language = 'podcasts.validation.language';
  }
  if (!cfg.tones.some((tone) => tone.code === values.tone)) errors.tone = 'podcasts.validation.tone';

  const seconds = Number(values.seconds);
  if (!Number.isInteger(seconds) || seconds < MIN_SECONDS || seconds > MAX_SECONDS) {
    errors.seconds = 'podcasts.validation.seconds';
  }

  const hostIds = cfg.hosts.map((host) => host.id);
  if (!hostIds.includes(values.hostA) || !hostIds.includes(values.hostB)) {
    errors.hosts = 'podcasts.validation.hostUnknown';
  } else if (values.hostA === values.hostB) {
    errors.hosts = 'podcasts.validation.hostsSame';
  }

  return errors;
};

// Payload dla createPodcast: dokładnie jedno źródło + opcje.
export const buildPodcastPayload = (values) => {
  const payload = {
    language: values.language,
    tone: values.tone,
    seconds: Number(values.seconds),
    hostA: values.hostA,
    hostB: values.hostB
  };
  if (values.sourceType === 'url') payload.url = values.url.trim();
  if (values.sourceType === 'text') {
    payload.text = normalizeText(values.text);
    const title = (values.title || '').trim();
    if (title) payload.title = title;
  }
  if (values.sourceType === 'file') payload.file = values.file;
  return payload;
};

// Stan każdego kroku: 'complete' | 'current' | 'failed' | 'pending'.
export const getStepStates = (job) => {
  if (!job) return PIPELINE_STEPS.map(() => 'pending');
  if (job.status === 'done' || job.step === 'done') return PIPELINE_STEPS.map(() => 'complete');

  const currentIndex = PIPELINE_STEPS.findIndex((step) => step.key === job.step);
  const progress = Number(job.progress) || 0;

  if (job.status === 'failed' || job.step === 'failed') {
    // Nie wiemy dokładnie, gdzie padło: kroki poniżej postępu uznajemy za zrobione,
    // pierwszy niezakończony oznaczamy jako błąd.
    let failedMarked = false;
    return PIPELINE_STEPS.map((step, index) => {
      const next = PIPELINE_STEPS[index + 1];
      const finished = step.key !== 'done' && next && progress >= next.progress;
      if (finished) return 'complete';
      if (!failedMarked) {
        failedMarked = true;
        return 'failed';
      }
      return 'pending';
    });
  }

  return PIPELINE_STEPS.map((step, index) => {
    if (currentIndex === -1) return 'pending';
    if (index < currentIndex) return 'complete';
    if (index === currentIndex) return 'current';
    return 'pending';
  });
};

export const clampProgress = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

export const formatDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  if (!total) return null;
  const minutes = Math.floor(total / 60);
  const rest = String(total % 60).padStart(2, '0');
  return `${minutes}:${rest}`;
};

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (error) {
    return url || null;
  }
};

export const getJobTitle = (job) =>
  job?.title || job?.sourceTitle || job?.sourceName || job?.url || null;

// Typ źródła (starsze zadania nie mają sourceType -> url).
export const getSourceType = (job) => job?.sourceType || (job?.url ? 'url' : 'text');

// Krótki opis źródła do tabeli / szczegółów.
export const getSourceDetail = (job) => {
  const type = getSourceType(job);
  if (type === 'url') return hostnameOf(job?.url);
  return job?.sourceName || null;
};
